import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Container, Row, Col } from "react-bootstrap";

import { Dialog } from "primereact/dialog";
import { Galleria } from "primereact/galleria";

import { Typography, Button, Theme, Card, CardContent, CardMedia, Skeleton } from "@mui/material";

import { createStyles, makeStyles } from "@mui/styles";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

import { IdName } from "../../shared/interfaces/Asset";
import { MediaMetaData } from "../../shared/interfaces/Result";

import { MediaFilters, FilterState } from "./MediaFilters";

import { useMedia, Media } from "./useMedia";

interface Props {
  mediaMetaData: MediaMetaData[];
  open: boolean;
  selectedAsset?: IdName;
  isPartialResult: boolean | undefined;
  onClose: () => void;
}

const mediaHeight = 460;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 345,
      minWidth: 345,
    },
    cardMedia: {
      minHeight: mediaHeight,
    },
    errorContainer: {
      margin: "auto",
      backgroundColor: theme.palette.darkBackground.light,
      padding: "1rem 1.5rem 1.5rem",
      height: "319px",
    },
    errorMessage: {
      display: "flex",
      justifyContent: "center",
      color: theme.palette.darkBackground.contrastText,
    },
    warningIcon: {
      color: theme.palette.warning.main,
      fontSize: "3em",
    },
  }),
);
export function MediaViewer(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const galleria = useRef<Galleria>(null);

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | undefined>();

  const [filters, setFilters] = useState<FilterState>({
    media: {
      photos: true,
      videos: true,
    },
    assets: props.selectedAsset ? [props.selectedAsset] : [],
  });

  const { media, imageList } = useMedia({
    mediaMetaData: props.mediaMetaData,
    filters: filters,
    isPartialResult: props.isPartialResult,
  });

  useEffect(() => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        assets: props.selectedAsset ? [props.selectedAsset] : [],
      };
    });
  }, [props.open, props.selectedAsset]);

  const handleFilterChange = useCallback((updatedFilters: Partial<FilterState>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
  }, []);

  const header = <Typography variant="h5" component="p">{`${t("common.media")} `}</Typography>;
  const footer = (
    <div className="d-flex flex-row align-items-center justify-content-end">
      <Button variant="contained" color="primary" className="ml-1" onClick={props.onClose}>
        {t("common.close")}{" "}
      </Button>
    </div>
  );

  function openImageInGalleria(media: Media) {
    if (media.isImage) {
      setSelectedImageIndex(media.imageIndex);
      galleria?.current?.show();
    }
  }

  function handleItemChange(event: any) {
    setSelectedImageIndex(event.index);
  }

  function itemTemplate(item: Media) {
    return (
      <img src={item.url} alt={item.SurveyAssetName} style={{ height: "100%", maxHeight: "100vh", display: "block" }} />
    );
  }

  function captionTemplate(item: MediaMetaData) {
    return (
      <>
        <Typography variant="h5">{item.SurveyAssetName}</Typography>
        <Typography variant="body2">{t("common.formattedDate", { dateString: item.Time })}</Typography>
        <Typography variant="body2">{item.Comment}</Typography>
      </>
    );
  }

  return (
    <Dialog
      header={header}
      visible={props.open}
      onHide={props.onClose}
      breakpoints={{ "960px": "75vw", "1920px": "85vw" }}
      style={{ width: "65vw" }}
      contentStyle={{ height: "100vh" }}
      footer={footer}
    >
      <Galleria
        ref={galleria}
        value={imageList}
        activeIndex={selectedImageIndex}
        onItemChange={handleItemChange}
        circular
        fullScreen
        showItemNavigators
        showThumbnails={false}
        item={itemTemplate}
        caption={captionTemplate}
      />
      <Container fluid>
        <Row>
          <Col xs={12} sm={2} className="px-0">
            {media && (
              <MediaFilters
                mediaMetaData={media}
                filters={filters}
                onFilterChange={handleFilterChange}
                isPartialResult={props.isPartialResult}
              />
            )}
          </Col>
          <Col>
            <Container>
              <Row>
                {media?.map((mediaItem) => {
                  if (mediaItem.display) {
                    let cardMediaComponentType: React.ElementType = "div";
                    let enableControls = false;

                    if (mediaItem.mimeType && mediaItem.mimeType.includes("image")) {
                      cardMediaComponentType = "img";
                    } else if (mediaItem.mimeType && mediaItem.mimeType.includes("video")) {
                      cardMediaComponentType = "video";
                      enableControls = true;
                    }

                    return (
                      <Col key={`${mediaItem.MediaFilePath}-cell`} className="p-1">
                        <Card sx={{ width: 345 }}>
                          {mediaItem.loading ? (
                            <Skeleton variant="rectangular" height={mediaHeight} />
                          ) : mediaItem.error ? (
                            <CardMedia className={classes.errorContainer}>
                              <span className={classes.errorMessage}>
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  className={`mt-5 mb-3 ${classes.warningIcon}`}
                                />
                              </span>
                              <span className={classes.errorMessage}>
                                <Typography variant={"subtitle1"} className="mb-4">
                                  {t("common.error")}
                                </Typography>
                              </span>
                              <span className={classes.errorMessage}>
                                <Typography variant={"caption"}>
                                  {mediaItem.error.status === 403
                                    ? t("common.operationForbiddenError", {
                                        operation: t("common.view"),
                                        item: "$t(common.media)",
                                      })
                                    : t("common.dataFetchError", {
                                        val: "$t(common.media)",
                                      })}
                                </Typography>
                              </span>
                            </CardMedia>
                          ) : (
                            <CardMedia
                              component={cardMediaComponentType}
                              onClick={() => {
                                openImageInGalleria(mediaItem);
                              }}
                              sx={{
                                cursor: mediaItem.isImage ? "pointer" : "inherit",
                              }}
                              image={mediaItem.url}
                              title={mediaItem.SurveyAssetName}
                              controls={enableControls}
                              className={classes.cardMedia}
                            />
                          )}

                          <CardContent>{captionTemplate(mediaItem)}</CardContent>
                        </Card>
                      </Col>
                    );
                  } else return null;
                })}
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </Dialog>
  );
}
